import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CaretDown, CircleNotch, PencilSimple, Plus, Trash, User as UserIcon, UsersThree, X } from '@phosphor-icons/react';

import {
  deleteMemberPhoto,
  getMember,
  listDependents,
  listGuardians,
  listMemberTeams,
  memberPhotoURL,
  unlinkGuardian,
  updateGuardianRelationship,
  uploadMemberPhoto,
  type GuardianRelationship,
  type MemberDetail,
  type MemberTeamAssignment,
  type MemberTeamRole,
} from '@/api/members';
import { ApiError } from '@/api/client';
import { AddGuardianModal } from '@/components/AddGuardianModal';
import { useConfirm } from '@/components/ConfirmDialog';
import { useToast } from '@/components/Toast';
import { ImageCropDialog } from '@/components/ImageCropDialog';
import { listTeams, type Team } from '@/api/teams';
import { listAudit } from '@/api/audit';
import { listMyOrganizations } from '@/api/orgs';
import { approveCommand, createCommand } from '@/api/commands';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { isValidEmail } from '@/lib/contact';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { resolve as resolveTeamColor } from '@/lib/teamColor';
import { config } from '@/config';

// MemberCard is the foldout side-panel view of a single member: full
// detail, editable, with avatar upload. Opened from the Members table
// (3-dot row menu → Edit) and reusable from any future surface (activity
// log click-through, AI command results, etc.).
//
// Edit semantics: all field changes batch into one POST /commands with a
// member.update Operation, so saving lands in the activity log and is
// revertable for 30 days. Photo upload is its own POST /photo endpoint
// (not via CommandIntent — binary uploads don't fit the JSON envelope)
// and writes its own audit row.

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  memberID: string | null;
  // Optional callback for when the user clicks a related member (a
  // guardian or a dependent) inside the Relations tab. The parent
  // typically swaps the card's memberID via setState; if absent, the
  // related-member rows still render but can't be navigated to.
  onOpenMember?: (memberID: string) => void;
}

interface FormState {
  first_name: string;
  last_name: string;
  email: string;
  city: string;
  phone: string;
  street_address: string;
  postal_code: string;
  personal_id: string;
  // "male" | "female" | "other" | "" (not recorded). Optional demographic tag.
  gender: string;
  // Mirrors guardian_public_consent_at on the server (boolean form).
  // Editable in the Profile tab via a checkbox the UI only renders for
  // under-18 members.
  guardian_public_consent: boolean;
  // Mirrors members.member.no_email. When true, the email input is
  // disabled and the row is excluded from the missing-email assistant
  // suggestion.
  no_email: boolean;
}

const emptyForm: FormState = {
  first_name: '',
  last_name: '',
  email: '',
  city: '',
  phone: '',
  street_address: '',
  postal_code: '',
  personal_id: '',
  gender: '',
  guardian_public_consent: false,
  no_email: false,
};

function memberToForm(m: MemberDetail): FormState {
  return {
    first_name: m.first_name,
    last_name: m.last_name,
    email: m.email ?? '',
    city: m.city ?? '',
    phone: m.phone ?? '',
    street_address: m.street_address ?? '',
    postal_code: m.postal_code ?? '',
    personal_id: m.personal_id ?? '',
    gender: m.gender ?? '',
    guardian_public_consent: m.guardian_public_consent,
    no_email: m.no_email,
  };
}

// isMinor reports whether the given YYYY-MM-DD birth date is under 18
// years before today. The Profile tab uses it to decide whether to
// render the photo-consent toggle — adults' photos always appear on
// the public roster, so the toggle would be confusing for them.
// Returns false when birthDate is null/empty (= unknown age → assumed
// adult by the public-side renderer too).
function isMinor(birthDate: string | null): boolean {
  if (!birthDate) return false;
  const parsed = Date.parse(birthDate);
  if (Number.isNaN(parsed)) return false;
  const eighteenYearsAgo = new Date();
  eighteenYearsAgo.setFullYear(eighteenYearsAgo.getFullYear() - 18);
  return parsed > eighteenYearsAgo.getTime();
}

export function MemberCard({ open, onOpenChange, slug, memberID, onOpenMember }: Props) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const toast = useToast();

  // Photo cache-buster bumped on upload/delete so the proxied <img>
  // bypasses the browser cache after a change.
  const [photoVersion, setPhotoVersion] = useState(0);
  const [cropOpen, setCropOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // Live phone validity from PhoneInput's libphonenumber parse. Drives a
  // non-blocking warning only — phone is free-text by design, so an invalid
  // value never blocks save. Defaults true so a blank field isn't flagged.
  const [phoneValid, setPhoneValid] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Org country (lowercased ISO) for the phone input's default region. Read
  // from the cached my-orgs query; falls back to 'se' until it loads.
  const { data: orgs } = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: open && !!token,
  });
  const phoneCountry = useMemo(
    () => (orgs?.find((o) => o.slug === slug)?.country_code ?? 'se').toLowerCase(),
    [orgs, slug],
  );

  // Email must be a valid bare address before save — same rule the server's
  // member.update enforces, surfaced early. Skipped when the member is flagged
  // no_email (the field is disabled then) or when blank (clearing is allowed).
  const emailInvalid = !form.no_email && form.email.trim() !== '' && !isValidEmail(form.email);
  // Phone is non-blocking: warn only when there's a value and it doesn't parse.
  const phoneWarn = form.phone.trim() !== '' && !phoneValid;

  const memberQuery = useQuery({
    queryKey: ['member', slug, memberID],
    queryFn: () => getMember(token, slug, memberID!),
    enabled: open && !!memberID && !!token,
    refetchOnWindowFocus: false,
  });

  // Hydrate the form whenever a fresh row arrives (open, refetch, etc.).
  useEffect(() => {
    if (memberQuery.data) setForm(memberToForm(memberQuery.data));
  }, [memberQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async (next: FormState) => {
      if (!memberID) throw new Error('no member');
      const original = memberQuery.data;
      if (!original) throw new Error('no original');

      // Only send fields the user actually changed. Operation contract is
      // "nil = leave alone, non-nil = set" (UpdateMember in
      // internal/commands/operations/member_update.go), so omitting an
      // unchanged field skips server-side validation of it — important for
      // personal_id, which may be stored raw (status='needs_review') from
      // an import and would otherwise fail revalidation on every save.
      const args: Record<string, unknown> = { member_id: memberID };
      if (next.first_name !== original.first_name) args.first_name = next.first_name;
      if (next.last_name !== original.last_name) args.last_name = next.last_name;
      if (next.email !== (original.email ?? '')) args.email = next.email;
      if (next.city !== (original.city ?? '')) args.city = next.city;
      if (next.phone !== (original.phone ?? '')) args.phone = next.phone;
      if (next.street_address !== (original.street_address ?? ''))
        args.street_address = next.street_address;
      if (next.postal_code !== (original.postal_code ?? ''))
        args.postal_code = next.postal_code;
      if (next.personal_id !== (original.personal_id ?? ''))
        args.personal_id = next.personal_id;
      if (next.gender !== (original.gender ?? '')) args.gender = next.gender;
      if (next.guardian_public_consent !== original.guardian_public_consent)
        args.guardian_public_consent = next.guardian_public_consent;
      if (next.no_email !== original.no_email) args.no_email = next.no_email;

      // member_id-only payload = no changes. Skip the round-trip instead of
      // hitting the server's "no fields to update" rejection.
      if (Object.keys(args).length === 1) return;

      // CommandIntent + member.update Operation. The server snapshots the
      // current row state inside the tx and stores an inverse for revert.
      const intent = await createCommand(token, slug, {
        user_input: `Edit ${next.first_name} ${next.last_name}`,
        plan_summary: `Update member ${next.first_name} ${next.last_name}`,
        operations: [{ type: 'member.update', args }],
      });
      await approveCommand(token, slug, intent.id);
    },
    onSuccess: async () => {
      // Await the list + detail refetches before closing the sheet, otherwise
      // the user briefly sees the pre-save row underneath the closing
      // animation and reads it as "the list didn't update". commands cache
      // can refresh in the background — nothing visible depends on it
      // closing in sync.
      void queryClient.invalidateQueries({ queryKey: ['commands', slug] });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['members', slug] }),
        queryClient.invalidateQueries({ queryKey: ['member', slug, memberID] }),
        // The edited person may also be rendered as a nested guardian (or
        // dependent) row under another member — those rows come from the
        // ['guardians'/'dependents', slug, parentID] caches, NOT ['members'].
        // Invalidate by prefix so every parent's list refetches and the
        // changed field (e.g. email) shows without a hard refresh.
        queryClient.invalidateQueries({ queryKey: ['guardians', slug] }),
        queryClient.invalidateQueries({ queryKey: ['dependents', slug] }),
      ]);
      onOpenChange(false);
      toast.success(t('memberCard.saveSuccess'));
    },
    onError: (err) => {
      setErrorCode(err instanceof Error ? err.message : 'unknown');
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!memberID) throw new Error('no member');
      return uploadMemberPhoto(token, slug, memberID, file);
    },
    onSuccess: () => {
      setPhotoVersion((v) => v + 1);
      void queryClient.invalidateQueries({ queryKey: ['member', slug, memberID] });
      // The Members table renders an avatar bubble keyed on has_photo;
      // refetch the list so a brand-new photo flips the bubble from
      // initials to the actual image without a page reload.
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    },
    onError: (err) => {
      console.error('photo upload failed', err);
      setErrorCode('photo_upload_failed');
    },
  });

  const deletePhotoMutation = useMutation({
    mutationFn: async () => {
      if (!memberID) throw new Error('no member');
      return deleteMemberPhoto(token, slug, memberID);
    },
    onSuccess: () => {
      setPhotoVersion((v) => v + 1);
      void queryClient.invalidateQueries({ queryKey: ['member', slug, memberID] });
      // Mirror the upload path — clearing a photo flips the bubble
      // back to initials in the list.
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    },
  });

  function onPhotoPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setErrorCode('file_too_large');
      return;
    }
    if (!/^image\//.test(f.type)) {
      setErrorCode('unsupported_image_type');
      return;
    }
    setErrorCode(null);
    uploadMutation.mutate(f);
    // Reset the input so picking the same file again triggers a change.
    e.target.value = '';
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorCode(null);
    // Email is the only hard client-side gate (phone is warn-only). The inline
    // message under the field already explains it; the server re-validates
    // regardless. This just avoids a guaranteed-400 round-trip.
    if (emailInvalid) return;
    saveMutation.mutate(form);
  }

  const data = memberQuery.data;
  const fullName = data ? `${data.first_name} ${data.last_name}`.trim() : '';
  const initials = data
    ? (data.first_name.charAt(0) + data.last_name.charAt(0)).toUpperCase()
    : '?';
  const photoURL =
    data?.has_photo && memberID
      ? memberPhotoURL(slug, memberID, photoVersion)
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-2xl"
      >
        <SheetTitle className="sr-only">{t('memberCard.title')}</SheetTitle>

        {memberQuery.isPending && (
          <p className="px-4 pt-12 text-sm text-muted-foreground">
            {t('memberCard.loading')}
          </p>
        )}

        {memberQuery.isError && (
          <p
            role="alert"
            className="m-4 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {t('memberCard.loadError')}
          </p>
        )}

        {data && (
          <div className="flex flex-col gap-5 p-4 pt-12 sm:p-6 sm:pt-12">
            {/* Avatar + name header */}
            <div className="flex flex-col items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept={Object.keys(ACCEPT_TYPES).join(',')}
                className="hidden"
                onChange={onPhotoPick}
                aria-label={t('memberCard.photoPicker')}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                className="group relative rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
                aria-label={
                  data.has_photo
                    ? t('memberCard.changePhoto')
                    : t('memberCard.addPhoto')
                }
              >
                <AuthedAvatar
                  photoURL={photoURL}
                  initials={initials}
                  token={token}
                  alt={fullName}
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0 rounded-full bg-foreground/0 transition-colors group-hover:bg-foreground/10"
                />
                <span
                  aria-hidden="true"
                  className="absolute bottom-1 right-1 flex h-9 w-9 items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-110"
                >
                  <Plus className="h-4 w-4" weight="bold" />
                </span>
              </button>
              {data.has_photo && (
                <div className="flex flex-row gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCropOpen(true)}
                  >
                    {t('imageInput.crop')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => deletePhotoMutation.mutate()}
                    disabled={deletePhotoMutation.isPending}
                  >
                    <Trash className="h-4 w-4" />
                    {t('memberCard.removePhoto')}
                  </Button>
                </div>
              )}
              {data.has_photo && memberID && (
                <MemberPhotoCrop
                  open={cropOpen}
                  photoURL={memberPhotoURL(slug, memberID, photoVersion)}
                  token={token}
                  onCropped={async (file) => {
                    await uploadMutation.mutateAsync(file);
                  }}
                  onClose={() => setCropOpen(false)}
                />
              )}
              <h2 className="font-heading text-xl font-bold tracking-tight">
                {fullName || t('memberCard.unnamed')}
              </h2>
            </div>

            <Tabs defaultValue="profile" className="flex flex-col gap-5">
              <TabsList className="self-center">
                <TabsTrigger value="profile">{t('memberCard.tab.profile')}</TabsTrigger>
                <TabsTrigger value="teams">{t('memberCard.tab.teams')}</TabsTrigger>
                <TabsTrigger value="relations">{t('memberCard.tab.relations')}</TabsTrigger>
                <TabsTrigger value="logs">{t('memberCard.tab.logs')}</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <form onSubmit={onSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Field
                      label={t('members.column.firstName')}
                      value={form.first_name}
                      onChange={(v) => setForm((f) => ({ ...f, first_name: v }))}
                      required
                    />
                    <Field
                      label={t('members.column.lastName')}
                      value={form.last_name}
                      onChange={(v) => setForm((f) => ({ ...f, last_name: v }))}
                      required
                    />
                    <div className="flex flex-col gap-1 sm:col-span-2">
                      <label className="flex flex-col gap-1">
                        <span className="text-xs font-semibold text-muted-foreground">
                          {t('members.column.email')}
                        </span>
                        <Input
                          type="email"
                          value={form.email}
                          disabled={form.no_email}
                          aria-invalid={emailInvalid || undefined}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, email: e.target.value }))
                          }
                        />
                      </label>
                      {emailInvalid && (
                        <p role="alert" className="text-xs text-destructive">
                          {t('members.form.invalidEmail')}
                        </p>
                      )}
                      <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <Checkbox
                          checked={form.no_email}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, no_email: e.target.checked }))
                          }
                          aria-label={t('memberCard.noEmail.label')}
                        />
                        <span>{t('memberCard.noEmail.label')}</span>
                      </label>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {t('members.column.phone')}
                      </span>
                      <PhoneInput
                        value={form.phone}
                        defaultCountry={phoneCountry}
                        aria-label={t('members.column.phone')}
                        onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                        onValidityChange={setPhoneValid}
                      />
                      {phoneWarn && (
                        <p className="text-xs text-muted-foreground">
                          {t('members.form.invalidPhoneWarning')}
                        </p>
                      )}
                    </div>
                    <Field
                      label={t('members.column.personalId')}
                      value={form.personal_id}
                      onChange={(v) => setForm((f) => ({ ...f, personal_id: v }))}
                    />
                    <Field
                      label={t('members.column.streetAddress')}
                      value={form.street_address}
                      onChange={(v) => setForm((f) => ({ ...f, street_address: v }))}
                      colSpan={2}
                    />
                    <Field
                      label={t('members.column.postalCode')}
                      value={form.postal_code}
                      onChange={(v) => setForm((f) => ({ ...f, postal_code: v }))}
                    />
                    <Field
                      label={t('members.column.city')}
                      value={form.city}
                      onChange={(v) => setForm((f) => ({ ...f, city: v }))}
                    />
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-muted-foreground">
                        {t('members.form.gender.label')}
                      </span>
                      {/* Radix reserves the empty string as "no value", so the
                          unset option travels as the __unset__ sentinel and maps
                          back to '' on the way into the form (same convention as
                          RulesForm / ActivityFormModal). */}
                      <Select
                        value={form.gender === '' ? '__unset__' : form.gender}
                        onValueChange={(v) =>
                          setForm((f) => ({ ...f, gender: v === '__unset__' ? '' : v }))
                        }
                      >
                        <SelectTrigger aria-label={t('members.form.gender.label')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset__">{t('members.form.gender.unset')}</SelectItem>
                          <SelectItem value="female">{t('members.form.gender.female')}</SelectItem>
                          <SelectItem value="male">{t('members.form.gender.male')}</SelectItem>
                          <SelectItem value="other">{t('members.form.gender.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Photo-consent toggle: only meaningful for under-18
                      members. Adults' photos show on the public roster
                      by default — no consent gate. Minors hide by default;
                      a guardian flips this on to publish the photo. The
                      row itself (name, jersey, position) appears on the
                      roster regardless. */}
                  {isMinor(memberQuery.data?.birth_date ?? null) && (
                    <label className="flex items-start gap-3 rounded-2xl border border-border bg-card px-4 py-3">
                      <Checkbox
                        checked={form.guardian_public_consent}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            guardian_public_consent: e.target.checked,
                          }))
                        }
                        className="mt-0.5"
                        aria-label={t('memberCard.photoConsent.label', {
                          defaultValue: 'Show photo on public roster',
                        })}
                      />
                      <span className="flex flex-col gap-1">
                        <span className="font-heading text-sm font-semibold">
                          {t('memberCard.photoConsent.label', {
                            defaultValue: 'Show photo on public roster',
                          })}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {t('memberCard.photoConsent.hint', {
                            defaultValue:
                              "Under 18 — photo is hidden by default. Toggle on once a guardian has consented to the photo appearing on the club's public site.",
                          })}
                        </span>
                      </span>
                    </label>
                  )}

                  {errorCode && (
                    <p
                      role="alert"
                      className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
                    >
                      {t(`memberCard.error.${errorCode}`, {
                        defaultValue: t('memberCard.error.unknown'),
                      })}
                    </p>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => onOpenChange(false)}
                      disabled={saveMutation.isPending}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" disabled={saveMutation.isPending || emailInvalid}>
                      {saveMutation.isPending ? t('common.submitting') : t('memberCard.save')}
                    </Button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="teams">
                {memberID && (
                  <TeamsTab
                    slug={slug}
                    memberID={memberID}
                    onClose={() => onOpenChange(false)}
                  />
                )}
              </TabsContent>

              <TabsContent value="relations">
                {memberID && (
                  <RelationsTab
                    slug={slug}
                    memberID={memberID}
                    {...(onOpenMember ? { onOpenMember } : {})}
                  />
                )}
              </TabsContent>

              <TabsContent value="logs">
                {memberID && <LogsTab slug={slug} memberID={memberID} />}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// LogsTab lists every activity-log entry ABOUT this member (the "to whom"
// flag), newest first — including a family invoice that's about several
// children. Backed by core.audit_log_subject via /audit?subject_member_id=.
function LogsTab({ slug, memberID }: { slug: string; memberID: string }) {
  const { t, i18n } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const logsQuery = useQuery({
    queryKey: ['member-logs', slug, memberID],
    queryFn: () => listAudit(token, slug, { subjectMemberId: memberID, limit: 50 }),
    enabled: !!token && !!slug && !!memberID,
  });

  const fmt = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  if (logsQuery.isPending) {
    return <p className="py-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>;
  }
  const entries = logsQuery.data?.items ?? [];
  if (entries.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-muted-foreground">
        {t('memberCard.logs.empty', { defaultValue: 'No activity recorded for this member yet.' })}
      </p>
    );
  }
  return (
    <ul className="flex flex-col divide-y divide-border">
      {entries.map((e) => {
        const summary = t(
          `settings.activity.action.${e.action}.summary`,
          (e.metadata ?? {}) as Record<string, unknown>,
        );
        const label = summary.includes('{{') ? e.summary : summary;
        return (
          <li key={e.id} className="flex flex-col gap-0.5 py-2.5">
            <span className="text-sm text-foreground">{label || e.summary}</span>
            <span className="text-xs text-muted-foreground">
              {fmt(e.created_at)}
              {e.actor_name ? ` · ${e.actor_name}` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

// TEAM_ROLES is the closed set the CHECK constraint on
// members.team_member.role enforces. Mirrors validTeamRoles in the Go
// operation so the SPA can't post a role the server would reject.
const TEAM_ROLES: MemberTeamRole[] = ['athlete', 'leader'];

// EditableAssignment is a row in the staged-edit list. Existing rows
// carry the assignment_id from the server (used as a stable React key
// for diffs); newly-added rows leave it undefined and the server picks
// the team_member.id when the Operation INSERTs. jersey_number is `null`
// when unset (matches the wire shape); position is "" when unset.
// public_visibility opts the row into the public team roster.
interface EditableAssignment {
  assignment_id?: string;
  team_id: string;
  team_name: string;
  team_color: string;
  role: MemberTeamRole;
  jersey_number: number | null;
  position: string;
  public_visibility: boolean;
}

function toEditable(a: MemberTeamAssignment): EditableAssignment {
  return {
    assignment_id: a.assignment_id,
    team_id: a.team_id,
    team_name: a.team_name,
    team_color: a.team_color,
    role: a.role,
    jersey_number: a.jersey_number,
    position: a.position,
    public_visibility: a.public_visibility,
  };
}

// canonicalKey turns an assignment into a (team_id, role) string used for
// dirty-state diff and de-duping the staged list. Matches the Operation's
// notion of identity (the unique partial index on team_member). Jersey
// and position are NOT part of identity — they're attributes of the row.
function canonicalKey(a: { team_id: string; role: MemberTeamRole }): string {
  return `${a.team_id}::${a.role}`;
}

interface TeamsTabProps {
  slug: string;
  memberID: string;
  onClose: () => void;
}

function TeamsTab({ slug, memberID, onClose }: TeamsTabProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const teamsQuery = useQuery({
    queryKey: ['teams', slug],
    queryFn: () => listTeams(token, slug),
    enabled: !!token,
    refetchOnWindowFocus: false,
  });

  const assignmentsQuery = useQuery({
    queryKey: ['member-teams', slug, memberID],
    queryFn: () => listMemberTeams(token, slug, memberID),
    enabled: !!token,
    refetchOnWindowFocus: false,
  });

  const [staged, setStaged] = useState<EditableAssignment[]>([]);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Sync staged state from the server snapshot whenever it lands. This
  // also runs after a successful save so the dirty indicator clears.
  useEffect(() => {
    if (assignmentsQuery.data) {
      setStaged(assignmentsQuery.data.assignments.map(toEditable));
    }
  }, [assignmentsQuery.data]);

  const serverKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of assignmentsQuery.data?.assignments ?? []) {
      set.add(canonicalKey(a));
    }
    return set;
  }, [assignmentsQuery.data]);

  const stagedKeys = useMemo(() => {
    const set = new Set<string>();
    for (const a of staged) set.add(canonicalKey(a));
    return set;
  }, [staged]);

  // Team IDs already in the staged list, regardless of role. The "+ Add
  // team" picker uses this to hide teams the member is already on so the
  // same person can't be added to one team twice. Backend still allows
  // multi-role per (team, member) for legacy importer rows; the SPA just
  // doesn't offer it as a forward-creation path.
  const stagedTeamIDs = useMemo(() => {
    const set = new Set<string>();
    for (const a of staged) set.add(a.team_id);
    return set;
  }, [staged]);

  const dirty = useMemo(() => {
    if (serverKeys.size !== stagedKeys.size) return true;
    for (const k of stagedKeys) if (!serverKeys.has(k)) return true;
    // Identity sets match — but jersey/position may still differ.
    // Build a (team_id, role) → assignment map of the server snapshot
    // and compare jersey + position per staged row.
    const serverByKey = new Map<string, MemberTeamAssignment>();
    for (const a of assignmentsQuery.data?.assignments ?? []) {
      serverByKey.set(canonicalKey(a), a);
    }
    for (const a of staged) {
      const s = serverByKey.get(canonicalKey(a));
      if (!s) return true;
      if ((s.jersey_number ?? null) !== (a.jersey_number ?? null)) return true;
      if ((s.position ?? '') !== (a.position ?? '')) return true;
      if (s.public_visibility !== a.public_visibility) return true;
    }
    return false;
  }, [serverKeys, stagedKeys, staged, assignmentsQuery.data]);

  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data?.teams]);
  const teamByID = useMemo(() => {
    const m = new Map<string, Team>();
    for (const tm of teams) m.set(tm.id, tm);
    return m;
  }, [teams]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Build the wire shape the team.set_member_assignments operation
      // expects: a list of {team_id, role, jersey_number?, position?}.
      // Server diffs against the current state inside the executing tx
      // and INSERTs new rows, UPDATEs jersey/position on existing rows,
      // soft-deletes rows missing from the list.
      const intent = await createCommand(token, slug, {
        user_input: 'Edit team memberships',
        plan_summary: 'Update team memberships',
        operations: [
          {
            type: 'team.set_member_assignments',
            args: {
              member_id: memberID,
              assignments: staged.map((a) => {
                const out: {
                  team_id: string;
                  role: MemberTeamRole;
                  jersey_number?: number;
                  position?: string;
                  public_visibility?: boolean;
                } = { team_id: a.team_id, role: a.role };
                if (a.jersey_number !== null) out.jersey_number = a.jersey_number;
                if (a.position) out.position = a.position;
                // Always send the flag — server uses COALESCE so omitting
                // would leave the existing value, which is the wrong
                // semantics when the user explicitly toggled the switch.
                out.public_visibility = a.public_visibility;
                return out;
              }),
            },
          },
        ],
      });
      await approveCommand(token, slug, intent.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['member-teams', slug, memberID] });
      void queryClient.invalidateQueries({ queryKey: ['commands', slug] });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    },
    onError: (err) => {
      setErrorCode(err instanceof Error ? err.message : 'unknown');
    },
  });

  function addAssignment(teamID: string, role: MemberTeamRole) {
    if (!teamID) return;
    const team = teamByID.get(teamID);
    if (!team) return;
    const next: EditableAssignment = {
      team_id: teamID,
      team_name: team.name,
      team_color: team.color,
      role,
      jersey_number: null,
      position: '',
      public_visibility: true,
    };
    if (stagedKeys.has(canonicalKey(next))) return; // duplicate guard
    setStaged((s) => [...s, next]);
  }

  function removeAssignment(idx: number) {
    setStaged((s) => s.filter((_, i) => i !== idx));
  }

  function changeJersey(idx: number, raw: string) {
    setStaged((s) =>
      s.map((a, i) => {
        if (i !== idx) return a;
        const trimmed = raw.trim();
        if (trimmed === '') return { ...a, jersey_number: null };
        const n = parseInt(trimmed, 10);
        if (Number.isNaN(n) || n < 0 || n > 999) return a;
        return { ...a, jersey_number: n };
      }),
    );
  }

  function changePosition(idx: number, raw: string) {
    setStaged((s) =>
      s.map((a, i) => (i === idx ? { ...a, position: raw.slice(0, 64) } : a)),
    );
  }

  function changeVisibility(idx: number, visible: boolean) {
    setStaged((s) =>
      s.map((a, i) => (i === idx ? { ...a, public_visibility: visible } : a)),
    );
  }

  function changeRole(idx: number, role: MemberTeamRole) {
    setStaged((s) => {
      const target = s[idx];
      if (!target) return s;
      const cand: EditableAssignment = { ...target, role };
      // If swapping to a (team, role) that already exists, drop this row
      // instead of creating a duplicate the server would reject.
      if (s.some((a, i) => i !== idx && canonicalKey(a) === canonicalKey(cand))) {
        return s.filter((_, i) => i !== idx);
      }
      return s.map((a, i) => (i === idx ? cand : a));
    });
  }

  if (assignmentsQuery.isPending || teamsQuery.isPending) {
    return (
      <p className="text-sm text-muted-foreground">{t('memberCard.teams.loading')}</p>
    );
  }

  if (assignmentsQuery.isError || teamsQuery.isError) {
    return (
      <p
        role="alert"
        className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      >
        {t('memberCard.teams.loadError')}
      </p>
    );
  }

  if (teams.length === 0) {
    // Org has no teams at all — direct the user to create one first
    // rather than offering an "Add team" affordance that has nothing to
    // pick from.
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border bg-muted/40 p-8 text-center">
        <UsersThree className="h-10 w-10 text-muted-foreground" weight="duotone" />
        <p className="font-heading text-base font-semibold">
          {t('memberCard.teams.noTeamsTitle')}
        </p>
        <p className="max-w-sm text-sm text-muted-foreground">
          {t('memberCard.teams.noTeamsBody')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {staged.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-border bg-muted/40 p-6 text-center text-sm text-muted-foreground">
          {t('memberCard.teams.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {staged.map((a, idx) => {
            const palette = resolveTeamColor(a.team_name, a.team_color);
            return (
              <li
                key={a.assignment_id ?? `new-${idx}-${a.team_id}-${a.role}`}
                className="group flex flex-col gap-2 rounded-2xl border border-border bg-card px-4 py-3 transition-colors hover:border-border/60 hover:bg-card/80"
              >
                <div className="flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: palette.dot }}
                  />
                  <span className="flex-1 truncate font-heading font-semibold tracking-tight">
                    {a.team_name}
                  </span>
                  <RolePicker
                    value={a.role}
                    onChange={(r) => changeRole(idx, r)}
                    ariaLabel={t('memberCard.teams.roleAria', { team: a.team_name })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeAssignment(idx)}
                    aria-label={t('memberCard.teams.remove', { team: a.team_name })}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
                {/* Jersey number + position only make sense for athletes —
                    leaders don't carry a kit. Hide for that role
                    to keep the UI uncluttered. */}
                {a.role === 'athlete' && (
                  <div className="flex flex-col gap-2 pl-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{t('memberCard.teams.jerseyLabel', { defaultValue: 'Number' })}</span>
                        <Input
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={999}
                          value={a.jersey_number ?? ''}
                          onChange={(e) => changeJersey(idx, e.target.value)}
                          placeholder="—"
                          className="h-8 w-16 text-center"
                          aria-label={t('memberCard.teams.jerseyAria', {
                            team: a.team_name,
                            defaultValue: 'Jersey number for {{team}}',
                          })}
                        />
                      </label>
                      <label className="flex flex-1 items-center gap-2 text-xs text-muted-foreground">
                        <span>{t('memberCard.teams.positionLabel', { defaultValue: 'Position' })}</span>
                        <Input
                          type="text"
                          value={a.position}
                          onChange={(e) => changePosition(idx, e.target.value)}
                          placeholder={t('memberCard.teams.positionPlaceholder', {
                            defaultValue: 'e.g. Goalkeeper',
                          })}
                          maxLength={64}
                          className="h-8 flex-1"
                          aria-label={t('memberCard.teams.positionAria', {
                            team: a.team_name,
                            defaultValue: 'Position for {{team}}',
                          })}
                        />
                      </label>
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox
                        checked={a.public_visibility}
                        onChange={(e) => changeVisibility(idx, e.target.checked)}
                        aria-label={t('memberCard.teams.visibilityAria', {
                          team: a.team_name,
                          defaultValue: 'Show on public roster for {{team}}',
                        })}
                      />
                      <span>
                        {t('memberCard.teams.visibilityLabel', {
                          defaultValue: 'Show on public roster',
                        })}
                      </span>
                    </label>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <AddTeamMenu
        teams={teams}
        excludedTeamIDs={stagedTeamIDs}
        onAdd={addAssignment}
      />

      {errorCode && (
        <p
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive"
        >
          {t(`memberCard.teams.error.${errorCode}`, {
            defaultValue: t('memberCard.teams.error.unknown'),
          })}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={saveMutation.isPending}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            setErrorCode(null);
            saveMutation.mutate();
          }}
          disabled={!dirty || saveMutation.isPending}
        >
          {saveMutation.isPending
            ? t('common.submitting')
            : t('memberCard.teams.save')}
        </Button>
      </div>
    </div>
  );
}

// RelationsTab shows the member's connections inside the family-link
// graph: guardians who watch out for them (top section) and dependents
// they are listed as a guardian for (bottom section). Both lists are
// read-only here — the canonical edit surfaces are the Members table's
// guardian inline-add (for guardians of an athlete) and the AI command
// bar. Clicking a row navigates the card to that related member via
// onOpenMember; without that prop the rows still render but are inert.
function RelationsTab({
  slug,
  memberID,
  onOpenMember,
}: {
  slug: string;
  memberID: string;
  onOpenMember?: (memberID: string) => void;
}) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;

  const guardiansQuery = useQuery({
    queryKey: ['guardians', slug, memberID],
    queryFn: () => listGuardians(token, slug, memberID),
    enabled: !!token && !!memberID,
    refetchOnWindowFocus: false,
  });
  const dependentsQuery = useQuery({
    queryKey: ['dependents', slug, memberID],
    queryFn: () => listDependents(token, slug, memberID),
    enabled: !!token && !!memberID,
    refetchOnWindowFocus: false,
  });

  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [addGuardianOpen, setAddGuardianOpen] = useState(false);
  // The guardian row currently open in the edit dialog (relationship + remove),
  // or null when the dialog is closed.
  const [editGuardian, setEditGuardian] = useState<RelationRow | null>(null);

  const isPending = guardiansQuery.isPending || dependentsQuery.isPending;
  const isError = guardiansQuery.isError || dependentsQuery.isError;
  const guardians = guardiansQuery.data?.guardians ?? [];
  const dependents = dependentsQuery.data?.dependents ?? [];

  // Member-name lookup: the dependent's own row data is the cheapest
  // source for the modal's "Add guardian for X" eyebrow. Falls back to
  // an empty string and the modal hides the name fragment in that case.
  const dependentName = useDependentDisplayName(slug, memberID, token);

  // Unlink one guardian. Confirmed via the design-system dialog (the
  // member_guardian row is soft-deleted server-side; the 30-day undo
  // window from CLAUDE.md applies, but the SPA doesn't surface a
  // separate undo CTA — the activity log carries the entry).
  const unlinkMut = useMutation({
    mutationFn: (guardianMemberID: string) =>
      unlinkGuardian(token, slug, memberID, guardianMemberID),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['guardians', slug, memberID] });
      void queryClient.invalidateQueries({ queryKey: ['members', slug] });
    },
  });

  async function onRemove(guardianMemberID: string, fullName: string) {
    const ok = await confirm({
      title: t('memberCard.relations.removeConfirmTitle', 'Remove this guardian?'),
      body: t('memberCard.relations.removeConfirmBody', {
        name: fullName,
        defaultValue: '{{name}} will no longer be linked as a guardian. The member record itself stays put — you can re-link them later.',
      }),
      confirmLabel: t('memberCard.relations.removeCta', 'Remove guardian'),
      tone: 'destructive',
    });
    if (!ok) return;
    unlinkMut.mutate(guardianMemberID);
  }

  if (isPending) {
    return (
      <p className="text-sm text-muted-foreground">{t('memberCard.relations.loading')}</p>
    );
  }
  if (isError) {
    return (
      <p
        role="alert"
        className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      >
        {t('memberCard.relations.loadError')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section>
        <header className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('memberCard.relations.guardiansTitle')}
          </h3>
          <Button type="button" variant="outline" size="sm" onClick={() => setAddGuardianOpen(true)}>
            <Plus weight="bold" className="h-4 w-4" />
            {t('memberCard.relations.addGuardian', 'Add guardian')}
          </Button>
        </header>
        {guardians.length === 0 ? (
          <p className="rounded-2xl border border-dashed border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            {t('memberCard.relations.guardiansEmpty', 'No guardians linked to this member.')}
          </p>
        ) : (
          <RelationSection
            slug={slug}
            token={token}
            rows={guardians.map((g) => ({
              memberID: g.member_id,
              firstName: g.first_name,
              lastName: g.last_name,
              relationship: g.relationship,
              email: g.email,
              phone: g.phone,
              hasPhoto: g.has_photo,
            }))}
            onEdit={setEditGuardian}
            {...(onOpenMember ? { onOpenMember } : {})}
          />
        )}
      </section>
      {dependents.length > 0 && (
        <RelationSection
          title={t('memberCard.relations.dependentsTitle')}
          slug={slug}
          token={token}
          rows={dependents.map((d) => ({
            memberID: d.member_id,
            firstName: d.first_name,
            lastName: d.last_name,
            relationship: d.relationship,
            email: d.email,
            phone: d.phone,
            hasPhoto: d.has_photo,
          }))}
          {...(onOpenMember ? { onOpenMember } : {})}
        />
      )}
      <AddGuardianModal
        open={addGuardianOpen}
        onClose={() => setAddGuardianOpen(false)}
        slug={slug}
        memberID={memberID}
        memberDisplayName={dependentName}
      />
      <EditRelationModal
        open={editGuardian !== null}
        guardian={editGuardian}
        slug={slug}
        token={token}
        memberID={memberID}
        onClose={() => setEditGuardian(null)}
        onRemove={(guardianMemberID, fullName) => {
          // Close the edit dialog first so the confirm dialog (also a native
          // <dialog> in the top layer) isn't stacked behind it.
          setEditGuardian(null);
          void onRemove(guardianMemberID, fullName);
        }}
      />
    </div>
  );
}

// useDependentDisplayName fetches the bare display name of the member
// the panel is for. Used by AddGuardianModal's title — the rest of the
// MemberCard already has this data via its parent `member` query, but
// RelationsTab is rendered as a tab and gets only `memberID`. A tiny
// extra fetch keeps the modal contract self-contained.
function useDependentDisplayName(
  slug: string,
  memberID: string,
  token: string | null,
): string {
  const q = useQuery({
    queryKey: ['member-detail-shallow', slug, memberID],
    queryFn: () => getMember(token, slug, memberID),
    enabled: !!token && !!memberID,
    staleTime: 60_000,
  });
  if (!q.data) return '';
  return `${q.data.first_name} ${q.data.last_name}`.trim();
}

// RELATIONSHIPS is the closed set the member_guardian.relationship CHECK
// constraint enforces — same list AddGuardianModal offers. The edit dialog
// reuses it for the relationship picker.
const RELATIONSHIPS: GuardianRelationship[] = [
  'parent',
  'grandparent',
  'sibling',
  'other_relative',
  'legal_guardian',
  'other',
];

interface RelationRow {
  memberID: string;
  firstName: string;
  lastName: string;
  relationship: GuardianRelationship;
  email: string;
  phone: string;
  hasPhoto: boolean;
}

// RelationAvatar is the small (40px) circular avatar shown at the start of a
// relations row. Fetches the member's photo with the Bearer token (member
// photos aren't public, so a bare <img src> can't load them) and falls back
// to initials — mirrors AuthedAvatar but row-sized.
function RelationAvatar({
  slug,
  memberID,
  hasPhoto,
  initials,
  token,
}: {
  slug: string;
  memberID: string;
  hasPhoto: boolean;
  initials: string;
  token: string | null;
}) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!hasPhoto || !token) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    let objectURL: string | null = null;
    void fetch(memberPhotoURL(slug, memberID), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`photo ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectURL = URL.createObjectURL(blob);
        setSrc(objectURL);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [slug, memberID, hasPhoto, token]);

  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="h-10 w-10 shrink-0 rounded-full border border-border object-cover"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-xs font-bold font-heading text-foreground/70"
    >
      {initials !== '?' ? initials : <UserIcon className="h-5 w-5" />}
    </div>
  );
}

function RelationSection({
  title,
  slug,
  token,
  rows,
  onOpenMember,
  onEdit,
}: {
  // Optional. When set, renders an h3 above the list. Guardians in the
  // new flow render the header inline (with the Add button) so they
  // pass `rows` only and skip the title.
  title?: string;
  slug: string;
  token: string | null;
  rows: RelationRow[];
  onOpenMember?: (memberID: string) => void;
  // When set, each row gets a pencil button that opens the edit dialog for
  // that row. Used for the guardians section; dependents stay read-only (the
  // link is edited from the other member's card).
  onEdit?: (row: RelationRow) => void;
}) {
  const { t } = useTranslation('common');
  return (
    <section>
      {title && (
        <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h3>
      )}
      <ul className="flex flex-col gap-2">
        {rows.map((r) => {
          const fullName = `${r.firstName} ${r.lastName}`.trim();
          const initials =
            ((r.firstName.charAt(0) || '') + (r.lastName.charAt(0) || '')).toUpperCase() || '?';
          const subline = [r.email, r.phone].filter(Boolean).join(' · ') || null;
          const relLabel = r.relationship
            ? t(`members.guardians.relationship.${r.relationship}`, {
                defaultValue: r.relationship,
              })
            : null;
          // Avatar + name + relationship pill — the navigable part of the row.
          // Kept as a unit so it can be a single click target (button) when
          // onOpenMember is set, sitting INSIDE the bordered card next to the
          // edit pencil (two buttons can't nest, so they're siblings here).
          const main = (
            <>
              <RelationAvatar
                slug={slug}
                memberID={r.memberID}
                hasPhoto={r.hasPhoto}
                initials={initials}
                token={token}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-sm font-semibold text-foreground">
                  {fullName || t('memberCard.unnamed')}
                </p>
                {subline && (
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{subline}</p>
                )}
              </div>
              {relLabel && (
                <span className="shrink-0 rounded-pill bg-muted px-2.5 py-1 text-xs font-semibold text-foreground/70">
                  {relLabel}
                </span>
              )}
            </>
          );
          return (
            <li
              key={r.memberID}
              className="flex items-center gap-2 rounded-2xl border border-border bg-card pr-2 transition-colors hover:border-foreground/20"
            >
              {onOpenMember ? (
                <button
                  type="button"
                  onClick={() => onOpenMember(r.memberID)}
                  className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl py-2.5 pl-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {main}
                </button>
              ) : (
                <div className="flex min-w-0 flex-1 items-center gap-3 py-2.5 pl-3">{main}</div>
              )}
              {onEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(r)}
                  aria-label={t('memberCard.relations.edit', 'Edit guardian {{name}}', {
                    name: fullName || t('memberCard.unnamed'),
                  })}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <PencilSimple className="h-4 w-4" weight="bold" aria-hidden />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// EditRelationModal edits one guardian link: change the relationship label
// (PATCH, direct human-as-self write) or remove the link entirely. Built on
// the native <dialog> like AddGuardianModal so focus-trap / escape / backdrop
// are browser-handled and the Select can portal into the dialog's top layer.
function EditRelationModal({
  open,
  guardian,
  slug,
  token,
  memberID,
  onClose,
  onRemove,
}: {
  open: boolean;
  guardian: RelationRow | null;
  slug: string;
  token: string | null;
  memberID: string;
  onClose: () => void;
  onRemove: (guardianMemberID: string, fullName: string) => void;
}) {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // '' (unknown) is a valid stored state but not an option the user can pick;
  // seed the picker with the current value and default to 'parent' when unset.
  const [relationship, setRelationship] = useState<GuardianRelationship>('parent');
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      setRelationship(guardian?.relationship || 'parent');
      setErrorCode(null);
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open, guardian]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return undefined;
    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);
    return () => el.removeEventListener('close', handleClose);
  }, [onClose]);

  const updateMut = useMutation({
    mutationFn: () => {
      if (!guardian) throw new Error('no guardian');
      return updateGuardianRelationship(token, slug, memberID, guardian.memberID, relationship);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['guardians', slug, memberID] });
      void queryClient.invalidateQueries({ queryKey: ['dependents', slug, memberID] });
      onClose();
    },
    onError: (err) => setErrorCode(err instanceof ApiError ? err.code : 'unknown'),
  });

  const fullName = guardian ? `${guardian.firstName} ${guardian.lastName}`.trim() : '';
  const initials = guardian
    ? ((guardian.firstName.charAt(0) || '') + (guardian.lastName.charAt(0) || '')).toUpperCase() || '?'
    : '?';
  // Compare against the raw stored value (not the seeded default) so a link
  // whose relationship is still unknown ("") can be assigned one: the picker
  // shows the suggested 'parent' and Save is enabled immediately.
  const dirty = guardian ? relationship !== guardian.relationship : false;

  return (
    <dialog
      ref={dialogRef}
      className="rounded-2xl border border-border bg-background p-0 shadow-2xl backdrop:bg-foreground/40"
    >
      <div className="flex w-[min(28rem,92vw)] flex-col gap-5 p-6">
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            {guardian && (
              <RelationAvatar
                slug={slug}
                memberID={guardian.memberID}
                hasPhoto={guardian.hasPhoto}
                initials={initials}
                token={token}
              />
            )}
            <div className="flex flex-col gap-0.5">
              <span className="font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('memberCard.relations.editEyebrow', 'Edit guardian')}
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground">
                {fullName || t('memberCard.unnamed')}
              </h2>
            </div>
          </div>
          <button
            type="button"
            aria-label={t('common.cancel')}
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </header>

        <div className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          <span>{t('memberCard.guardian.modal.fields.relationship', 'Relationship')}</span>
          <Select
            value={relationship}
            onValueChange={(v) => setRelationship(v as GuardianRelationship)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent container={dialogRef.current}>
              {RELATIONSHIPS.map((r) => (
                <SelectItem key={r} value={r}>
                  {t(`members.guardians.relationship.${r}`, { defaultValue: r })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {errorCode && (
          <p role="alert" className="text-sm text-destructive">
            {t(`memberCard.guardian.modal.error.${errorCode}`, {
              defaultValue: t('memberCard.guardian.modal.error.unknown'),
            })}
          </p>
        )}

        <footer className="flex items-center justify-between gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={updateMut.isPending}>
            {t('common.cancel')}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                if (guardian) onRemove(guardian.memberID, fullName || t('memberCard.unnamed'));
              }}
              disabled={updateMut.isPending}
            >
              <Trash className="h-4 w-4" weight="bold" />
              {t('memberCard.relations.removeCta', 'Remove guardian')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setErrorCode(null);
                updateMut.mutate();
              }}
              disabled={!dirty || updateMut.isPending}
            >
              {updateMut.isPending ? (
                <CircleNotch className="h-4 w-4 animate-spin motion-reduce:animate-none" weight="bold" />
              ) : null}
              {t('memberCard.relations.editSave', 'Save')}
            </Button>
          </div>
        </footer>
      </div>
    </dialog>
  );
}

// roleAccent maps a role to a tinted background + foreground used by the
// pill trigger. Keeps the look on-brand (no native browser chrome) while
// still giving each role a distinct visual identity at a glance.
const roleAccent: Record<MemberTeamRole, string> = {
  athlete:
    'bg-[oklch(0.97_0.06_140)] text-[oklch(0.35_0.12_140)] hover:bg-[oklch(0.94_0.08_140)]',
  leader:
    'bg-[oklch(0.97_0.05_50)] text-[oklch(0.4_0.13_50)] hover:bg-[oklch(0.94_0.08_50)]',
};

// RolePicker is the role label on each assignment row. Looks like a
// flat pill, opens a Radix DropdownMenu with one radio item per role —
// keeps interaction inside the design system (no native <select>).
function RolePicker({
  value,
  onChange,
  ariaLabel,
}: {
  value: MemberTeamRole;
  onChange: (r: MemberTeamRole) => void;
  ariaLabel: string;
}) {
  const { t } = useTranslation('common');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1 font-heading text-xs font-semibold tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${roleAccent[value]}`}
        >
          <span>{t(`memberCard.teams.role.${value}`)}</span>
          <CaretDown className="h-3 w-3" weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(v) => onChange(v as MemberTeamRole)}
        >
          {TEAM_ROLES.map((r) => (
            <DropdownMenuRadioItem key={r} value={r}>
              {t(`memberCard.teams.role.${r}`)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// AddTeamMenu is the "+ Add to a team" CTA. One button, one dropdown
// listing teams the member isn't already on. Picking a team appends a
// row defaulted to athlete; the user fine-tunes the role inline via the
// RolePicker on the new row.
//
// The list is filtered by team_id (not by (team_id, role)) so the same
// member can't be added to a single team twice. Backend still permits
// multi-role assignments — they only enter the system via the importer
// or other non-SPA paths today.
function AddTeamMenu({
  teams,
  excludedTeamIDs,
  onAdd,
}: {
  teams: Team[];
  excludedTeamIDs: Set<string>;
  onAdd: (teamID: string, role: MemberTeamRole) => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Reset the search box every time the menu re-opens so the user
  // doesn't see stale text from a prior open.
  useEffect(() => {
    if (open) {
      setSearch('');
      // Defer until after Radix has mounted the content, otherwise the
      // focus call lands before the input exists.
      const id = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  const available = useMemo(
    () => teams.filter((tm) => !excludedTeamIDs.has(tm.id)),
    [teams, excludedTeamIDs],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return available;
    return available.filter((tm) => tm.name.toLowerCase().includes(q));
  }, [available, search]);

  if (available.length === 0) {
    return (
      <p className="px-1 text-xs text-muted-foreground">
        {t('memberCard.teams.allAssigned')}
      </p>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-border bg-transparent px-4 py-3 font-heading text-sm font-semibold tracking-tight text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Plus className="h-4 w-4" weight="bold" />
          {t('memberCard.teams.addTeam')}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border p-2">
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('memberCard.teams.searchPlaceholder')}
            // Stop the dropdown's roving-focus from hijacking arrow / typeahead
            // keys while the user is editing the query.
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                return;
              }
              e.stopPropagation();
            }}
            className="w-full rounded-pill border border-border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('memberCard.teams.searchPlaceholder')}
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-3 text-center text-xs text-muted-foreground">
              {t('memberCard.teams.searchNoResults')}
            </p>
          ) : (
            filtered.map((team) => {
              const palette = resolveTeamColor(team.name, team.color);
              return (
                <DropdownMenuSub key={team.id}>
                  <DropdownMenuSubTrigger className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: palette.dot }}
                    />
                    <span className="flex-1 truncate">{team.name}</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuPortal>
                    <DropdownMenuSubContent className="min-w-[10rem]">
                      {TEAM_ROLES.map((r) => (
                        <DropdownMenuItem
                          key={r}
                          onSelect={() => onAdd(team.id, r)}
                        >
                          {t(`memberCard.teams.role.${r}`)}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuPortal>
                </DropdownMenuSub>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ACCEPT_TYPES = {
  'image/jpeg': true,
  'image/png': true,
  'image/webp': true,
} as const;

function Field({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  colSpan = 1,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  colSpan?: 1 | 2;
}) {
  return (
    <label className={colSpan === 2 ? 'flex flex-col gap-1 sm:col-span-2' : 'flex flex-col gap-1'}>
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      />
    </label>
  );
}

// AuthedAvatar fetches the photo URL with the user's Bearer token (the
// browser's default <img src> can't carry custom auth headers), turns
// the bytes into an object URL, and renders it. Falls back to the
// initials block when there's no photo or the fetch fails.
function AuthedAvatar({
  photoURL,
  initials,
  token,
  alt,
}: {
  photoURL: string | null;
  initials: string;
  token: string | null;
  alt: string;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!photoURL || !token) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    let objectURL: string | null = null;
    void fetch(photoURL, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`photo ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectURL = URL.createObjectURL(blob);
        setSrc(objectURL);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [photoURL, token]);

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className="h-32 w-32 rounded-full border border-border object-cover shadow-sm"
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      className="flex h-32 w-32 items-center justify-center rounded-full border border-border bg-muted text-3xl font-bold font-heading text-foreground/70"
    >
      {initials !== '?' ? initials : <UserIcon className="h-12 w-12" />}
    </div>
  );
}

// MemberPhotoCrop fetches the current photo (auth required — member
// photos are not public), wraps the bytes in a temporary blob: URL,
// and feeds that into the shared ImageCropDialog. On crop the new
// File is handed back to the caller's onCropped (which runs the
// member-photo upload mutation). The blob: URL is revoked on close.
function MemberPhotoCrop({
  open,
  photoURL,
  token,
  onCropped,
  onClose,
}: {
  open: boolean;
  photoURL: string;
  token: string | null;
  onCropped: (file: File) => Promise<void>;
  onClose: () => void;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSrc(null);
      return undefined;
    }
    let cancelled = false;
    let objectURL: string | null = null;
    void fetch(photoURL, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(async (r) => {
        if (!r.ok) throw new Error(`photo ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectURL = URL.createObjectURL(blob);
        setSrc(objectURL);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => {
      cancelled = true;
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, [open, photoURL, token]);

  if (!src) return null;
  return (
    <ImageCropDialog
      open={open}
      src={src}
      aspect={1 /* square headshots */}
      filename="member-photo"
      onCrop={onCropped}
      onClose={onClose}
    />
  );
}

// Re-export config so dynamic imports don't accidentally pull in
// node-only modules through tree-shaking edge cases.
export const __memberCardConfigUsed = config;
